import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Trash2, RotateCcw, ArrowLeft, Loader2, Search, AlertTriangle, Clock,
} from "lucide-react";
import { fmtDate, formatApiError } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import PageHero from "@/components/PageHero";

/**
 * iter156 — Event Trash.
 *
 * Deleted bookings live here for TRASH_TTL_DAYS (30) days before the
 * backend purges them on server startup. Two actions:
 *   • Restore  — clears deleted_at, event returns to /bookings.
 *   • Delete Forever — admin-only, hard-deletes + cascades pipeline task
 *                       cleanup.
 *
 * Search filters by client name / event type / venue.
 */
export default function BookingsTrash() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const isAdmin = user?.role === "admin";
  const canWrite = user?.role === "admin" || user?.role === "coordinator";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/trash");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load Trash");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((b) => {
      const hay = `${b.client_name} ${b.event} ${b.location} ${b.couple_name || ""}`.toLowerCase();
      return hay.includes(t);
    });
  }, [rows, q]);

  const restore = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/bookings/${id}/restore`);
      toast.success("Event restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not restore");
    } finally { setBusyId(null); }
  };

  const deleteForever = async (id) => {
    const b = rows.find((x) => x.id === id);
    const label = b ? `${b.client_name}${b.event ? " · " + b.event : ""}` : "this event";
    const ok = window.confirm(
      `Delete Forever?\n\n"${label}" and all its deliverable-pipeline tasks will be permanently removed.\n\nThis cannot be undone.\n\nContinue?`,
    );
    if (!ok) return;
    setBusyId(id);
    try {
      await api.delete(`/bookings/${id}/permanent`);
      toast.success("Event permanently deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="bookings-trash-page">
      <PageHero
        eyebrow="Kahani Studio · Event Management"
        title="Trash"
        subtitle="Deleted events wait here for 30 days before permanent removal. Restore anything that was moved here by mistake."
        right={
          <button className="btn btn-outline" onClick={() => navigate("/bookings")} data-testid="trash-back-to-bookings">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Back to Bookings
          </button>
        }
      />

      <div className="card p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.8} />
            <input
              type="text"
              className="input pl-9 w-full"
              placeholder="Search by client, event or venue…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="trash-search"
            />
          </div>
          <div className="ml-auto text-xs text-stone-500 inline-flex items-center gap-1.5" data-testid="trash-count">
            <Clock className="w-3.5 h-3.5" /> {filtered.length} deleted event{filtered.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="trash-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold text-left">
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3 w-32">Event Date</th>
                <th className="px-4 py-3 w-40">Deleted By</th>
                <th className="px-4 py-3 w-40">Deleted On</th>
                <th className="px-4 py-3 w-32">Purge In</th>
                <th className="px-4 py-3 w-56 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-stone-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading Trash…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-stone-400" data-testid="trash-empty">
                    <Trash2 className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.2} />
                    <div className="font-heading text-lg text-stone-500 mb-1">Trash is empty</div>
                    <div className="text-xs">Deleted events will appear here for 30 days.</div>
                  </td>
                </tr>
              )}
              {!loading && filtered.map((b) => {
                const purgeInDays = Number(b.purge_in_days ?? 30);
                const dangerZone = purgeInDays <= 3;
                return (
                  <tr key={b.id} data-testid={`trash-row-${b.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-heading font-semibold text-stone-900" data-testid={`trash-name-${b.id}`}>
                        {b.client_name || "—"}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5 space-x-2">
                        {b.event && <span>{b.event}</span>}
                        {b.location && <span>· {b.location}</span>}
                        {b.couple_name && <span>· {b.couple_name}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600 font-mono whitespace-nowrap">
                      {b.day1 ? fmtDate(b.day1) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">
                      {b.deleted_by_name || b.deleted_by || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600 whitespace-nowrap">
                      {b.deleted_at ? fmtDate(String(b.deleted_at).slice(0, 10)) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-[10px] uppercase tracking-wider ${dangerZone ? "bg-red-50 text-red-800 border border-red-200" : "bg-stone-100 text-stone-700"}`} data-testid={`trash-purge-${b.id}`}>
                        {dangerZone && <AlertTriangle className="w-3 h-3" strokeWidth={2} />}
                        {purgeInDays === 0 ? "Today" : `${purgeInDays}d`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {canWrite && (
                          <button
                            type="button"
                            disabled={busyId === b.id}
                            onClick={() => restore(b.id)}
                            className="btn btn-outline btn-sm inline-flex items-center gap-1.5"
                            data-testid={`trash-restore-${b.id}`}
                          >
                            {busyId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />}
                            Restore
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            disabled={busyId === b.id}
                            onClick={() => deleteForever(b.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-semibold hover:bg-red-800 transition disabled:opacity-50"
                            data-testid={`trash-delete-forever-${b.id}`}
                            title="Permanently delete (cannot be undone)"
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                            Delete Forever
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
